// src/services/authService.js
import { api, setAccessToken, getAccessToken } from "../api";

const pickUserForClient = (u = {}) => ({
  id: u.id || u._id,
  email: u.email,
  name: u.name,
  picture: u.picture,
  role: u.role,
  approvalStatus: u.approvalStatus,
  isAdmin: u.isAdmin,
  preferences: u.preferences || { dismissedTips: [] },
});

// For any fetch-based call, always use api.defaults.baseURL
async function postJson(
  path,
  body,
  { headers = {}, withCredentials = false } = {}
) {
  const url = `${api.defaults.baseURL}${
    path.startsWith("/") ? "" : "/"
  }${path}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    credentials: withCredentials ? "include" : "same-origin",
    body: JSON.stringify(body || {}),
  });

  let data = null;
  try {
    data = await res.json();
  } catch {}
  if (!res.ok) {
    const msg =
      (data && (data.message || data.error)) ||
      `${res.status} ${res.statusText}` ||
      "Request failed";
    throw new Error(msg);
  }
  return data;
}

function persistUserAndToken({ accessToken, user }) {
  // Put access token into memory for axios interceptor
  if (accessToken) setAccessToken(accessToken);
  // Back-compat for existing code
  if (accessToken) localStorage.setItem("token", accessToken);
  if (user)
    localStorage.setItem("user", JSON.stringify(pickUserForClient(user)));
}

export const authService = {
  // Email/password
  async login(credentials) {
    try {
      const { data } = await api.post("/auth/login", credentials, {
        withCredentials: true,
      });

      // Back-compat: backend returns accessToken (new) AND we also send token (old)
      const token = data?.accessToken || data?.token || null;
      if (data?.success && token) {
        persistUserAndToken({ accessToken: token, user: data.user });
      }
      return data;
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Login failed",
      };
    }
  },

  // Google login — supports ALL common payloads without changing your UI
  // Accepts:
  //  - GSI object { credential }
  //  - react-google-login { tokenId } (deprecated)
  //  - object with { accessToken }
  //  - raw string: ID token (JWT with dots) or OAuth access token
  async googleLogin(googleValue) {
    try {
      let payload = null;

      if (googleValue && typeof googleValue === "object") {
        if (googleValue.credential)
          payload = { credential: googleValue.credential }; // GSI ID token
        else if (googleValue.tokenId)
          payload = { id_token: googleValue.tokenId }; // old lib ID token
        else if (googleValue.accessToken)
          payload = { token: googleValue.accessToken }; // access token
      }

      if (!payload) {
        if (typeof googleValue === "string" && googleValue.includes(".")) {
          payload = { id_token: googleValue }; // likely an ID token (JWT)
        } else {
          payload = { token: googleValue || null }; // access token or null
        }
      }

      // Simple guard to avoid sending { token: null }
      if (!payload.credential && !payload.id_token && !payload.token) {
        return { success: false, message: "No Google token provided" };
      }

      const { data } = await api.post("/auth/google", payload, {
        withCredentials: true,
      });
      const token = data?.accessToken || data?.token || null;
      if (data?.success && token) {
        persistUserAndToken({ accessToken: token, user: data.user });
      }
      return data;
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Google login failed",
      };
    }
  },

  // Code flow (if you use popup+code)
  async googleLoginWithCode(code) {
    try {
      const data = await postJson(
        "/auth/google",
        { code },
        {
          headers: { "X-Requested-With": "XmlHttpRequest" },
          withCredentials: true,
        }
      );
      const token = data?.accessToken || data?.token || null;
      if (data?.success && token) {
        persistUserAndToken({ accessToken: token, user: data.user });
      }
      return data;
    } catch (error) {
      const apiData = error?.response?.data;
      if (apiData) {
        return apiData; // contains isNewUser, user, message, etc.
      }

      // Fallback for network/unknown errors
      return {
        success: false,
        message: error?.message || "Google auth failed",
      };
    }
  },

  // Facebook
  async facebookLogin(accessToken, userID) {
    try {
      const { data } = await api.post(
        "/auth/facebook",
        { accessToken, userID },
        {
          withCredentials: true,
          headers: { "X-Requested-With": "XmlHttpRequest" },
        }
      );
      const token = data?.accessToken || data?.token || null;
      if (data?.success && token) {
        persistUserAndToken({ accessToken: token, user: data.user });
      }
      return data;
    } catch (err) {
      return {
        success: false,
        message:
          err?.response?.data?.message ||
          err?.message ||
          "Facebook authentication failed.",
      };
    }
  },

  async register(userData) {
    try {
      const { data } = await api.post("/auth/register", userData);
      return data;
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Registration failed",
      };
    }
  },

  async sendSignupCode(userData) {
    try {
      const { data } = await api.post("/auth/signup/send-code", userData);
      return data;
    } catch (error) {
      return {
        success: false,
        message: error?.response?.data?.message || "Sending Signup code failed",
      };
    }
  },

  async verifySignupCode(userData) {
    try {
      const { data } = await api.post("/auth/signup/verify", userData);
      return data;
    } catch (error) {
      return {
        success: false,
        message:
          error?.response?.data?.message || "Verifying Signup code failed",
      };
    }
  },

  async forgotPassword(userData) {
    try {
      const { data } = await api.post("/auth/forgot-password", userData);
      return data;
    } catch (error) {
      return { success: false, message: "Error processing request" };
    }
  },

  async resetPasswordByToken(userData) {
    try {
      const { data } = await api.post(
        "/auth/reset-password-by-token",
        userData
      );
      return data;
    } catch (error) {
      return { success: false, message: "Error processing request" };
    }
  },

  async resetPassword(userData) {
    try {
      const token = this.getToken(); // optional
      const { data } = await api.post("/auth/reset-password", userData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      return data;
    } catch (error) {
      return { success: false, message: "Error processing request" };
    }
  },

  async logout() {
    try {
      await api.post(
        "/auth/logout",
        {},
        {
          withCredentials: true,
          headers: { "X-Requested-With": "XmlHttpRequest" },
        }
      );
    } catch (error) {
      console.warn("Logout request failed:", error);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setAccessToken(null);
  },

  getCurrentUser() {
    const s = localStorage.getItem("user");
    try {
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  },

  // Persist an updated user (e.g. after an avatar change) to localStorage so it
  // survives reloads. Caller should also update AuthContext via setUser().
  updateStoredUser(user) {
    if (!user) return;
    localStorage.setItem("user", JSON.stringify(pickUserForClient(user)));
  },

  getToken() {
    return getAccessToken() || localStorage.getItem("token");
  },
};
